import {
  evaCityDictionarySchema,
  type EvaCityDictionary
} from '@/models/api/eva-city-dictionary-response'
import { evaCityPairSchema, type EvaCityPair } from '@/models/api/eva-city-pair'
import {
  evaFlightSchedulesSchema,
  type EvaFlightSchedules
} from '@/models/api/eva-flight-schedules-response'
import type { CibsSearchAndBookFlightRequest } from '@/models/api/cibs-search-and-book-flight-request'
import {
  cibsSearchAndBookFlightResponseSchema,
  type CibsSearchAndBookFlightResponse
} from '@/models/api/cibs-search-and-book-flight-response'
import {
  apiValidator,
  wrapFetchResponse,
  wrapXmlResponse,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'
import { useEnv } from '~library/composables'
import { Locale } from '~library/enums'

export default class CibsApi {
  constructor(private readonly api: ApiFactory) {}

  /**
   * API 查詢航班及訂票
   * https://hackmd.io/Ut6rglkXTPW1x19GimPHZg
   */
  @apiValidator(cibsSearchAndBookFlightResponseSchema)
  searchAndBookFlight(
    data: CibsSearchAndBookFlightRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<CibsSearchAndBookFlightResponse>> {
    return this.api.post('Cibs/SearchAndBookFlight', data, config)
  }

  /**
   * API 取得城市對航線資料（呼叫長榮航空外部 API）
   * https://hackmd.io/M5x9X5pHQJ2mOGE06E1UMQ?both
   * @param locale 語系，預設為 'zh-TW'
   */
  @apiValidator(evaCityPairSchema)
  async getCityPairOnline(locale: Locale): Promise<ApiResponse<EvaCityPair>> {
    const {
      VITE_EVA_CITY_PAIR_ONLINE_ZH_TW_URL,
      VITE_EVA_CITY_PAIR_ONLINE_EN_US_URL,
      VITE_EVA_CITY_PAIR_ONLINE_ZH_CN_URL
    } = useEnv()

    const urlMap = {
      [Locale.ZH_TW]: VITE_EVA_CITY_PAIR_ONLINE_ZH_TW_URL,
      [Locale.EN]: VITE_EVA_CITY_PAIR_ONLINE_EN_US_URL,
      [Locale.ZH_CN]: VITE_EVA_CITY_PAIR_ONLINE_ZH_CN_URL
    }

    const url = urlMap[locale]

    if (!url) {
      throw new Error(
        `City Pair Online URL for locale ${locale} is not defined`
      )
    }

    const response = await fetch(url)

    return wrapFetchResponse<EvaCityPair>(response)
  }

  /**
   * API 取得城市對航線資料 - 多城市（呼叫長榮航空外部 API）
   * @param locale 語系，預設為 'zh-TW'
   */
  @apiValidator(evaCityPairSchema)
  async getCityPairMulti(locale: Locale): Promise<ApiResponse<EvaCityPair>> {
    const {
      VITE_EVA_CITY_PAIR_MULTI_ZH_TW_URL,
      VITE_EVA_CITY_PAIR_MULTI_EN_US_URL,
      VITE_EVA_CITY_PAIR_MULTI_ZH_CN_URL
    } = useEnv()

    const urlMap = {
      [Locale.ZH_TW]: VITE_EVA_CITY_PAIR_MULTI_ZH_TW_URL,
      [Locale.EN]: VITE_EVA_CITY_PAIR_MULTI_EN_US_URL,
      [Locale.ZH_CN]: VITE_EVA_CITY_PAIR_MULTI_ZH_CN_URL
    }

    const url = urlMap[locale]

    if (!url) {
      throw new Error(`City Pair Multi URL for locale ${locale} is not defined`)
    }

    const response = await fetch(url)

    return wrapFetchResponse<EvaCityPair>(response)
  }

  /**
   * API 取得城市字典（呼叫長榮航空外部 API）
   */
  @apiValidator(evaCityDictionarySchema)
  async getCityDictionary(): Promise<ApiResponse<EvaCityDictionary>> {
    const { VITE_EVA_CITY_DICTIONARY_URL } = useEnv()

    if (!VITE_EVA_CITY_DICTIONARY_URL) {
      throw new Error('VITE_EVA_CITY_DICTIONARY_URL is not defined')
    }

    const response = await fetch(VITE_EVA_CITY_DICTIONARY_URL)

    return wrapFetchResponse<EvaCityDictionary>(response)
  }

  /**
   * API 取得航班時刻表機場資料（呼叫長榮航空外部 API）
   */
  @apiValidator(evaFlightSchedulesSchema)
  async getFlightSchedules(): Promise<ApiResponse<EvaFlightSchedules>> {
    const { VITE_EVA_FLIGHT_SCHEDULES_URL } = useEnv()

    if (!VITE_EVA_FLIGHT_SCHEDULES_URL) {
      throw new Error('VITE_EVA_FLIGHT_SCHEDULES_URL is not defined')
    }

    const response = await fetch(VITE_EVA_FLIGHT_SCHEDULES_URL)

    return wrapXmlResponse<EvaFlightSchedules>(response)
  }
}
